<template>
  <div>
    <!-- LOADING API DATA -->
    <div v-if="loading">
      <i class="fas fa-spin fa-fw fa-sync centered"></i>
    </div>

    <!-- PAGE TURNER NOT FOUND OR NOT READY -->
    <div v-else-if="!data" class="centered text-center">
      <i class="fas fa-exclamation-triangle fa-3x mb-2"></i>
      <div>Page Turner Not Found</div>
    </div>

    <!-- PAGE TURNER READY -->
    <div
      v-else
      class="container"
      :class="{'hide-cursor': !showUI}"
      ref="container"
      @mousewheel="changeZoom"
      @DOMMouseScroll="changeZoom"
    >
      <!-- HEADER WITH AGENT COLOUR AND LOGO -->
      <header class="header" :style="`background-color: ${ data.style.colour1_page_turners || data.style.colour1}`">
        <img
          :src="`${data.style.logo_page_turners_url || data.style.logo_url}`"
          :alt="data.name"
          class="logo"
        />
      </header>

      <!-- PREVIOUS PAGE -->
      <transition name="hide">
        <i
          class="fas fa-chevron-left btn-prev"
          title="Previous Page"
          @click="previous"
          v-if="activePage > (viewportOrientation === 'landscape' ? 1 : 0) && showUI && zoomImage === null"
        ></i>
      </transition>

      <!-- NEXT PAGE -->
      <transition name="hide">
        <i
          class="fas fa-chevron-right btn-next"
          title="Next Page"
          @click="next"
          v-if="activePage < data.full_pages.length - 1 && showUI && zoomImage === null"
        ></i>
      </transition>

      <!-- USAGE INSTRUCTIONS -->
      <section
        class="instructions"
        v-if="zoomImage === null && viewportOrientation === 'landscape' && !isPlaying"
      >
        <h2>Here's how to make the most of our digital page-turner:</h2>
        <ul class="fa-ul">
          <li>
            <i class="fas fa-li fa-chevron-right"></i> Click the arrows on the side of the screen to advance pages..
          </li>
          <li>
            <i class="fas fa-li fa-search-plus"></i> Double click a page to zoom in.
          </li>
          <li>
            <i class="fas fa-li fa-arrows-alt"></i> Once zoomed in, scroll your mouse wheel to control the zoom level. Drag your mouse to pan it around the screen.
          </li>
          <li>
            <i class="fas fa-li fa-mouse-pointer"></i> Move your cursor to the bottom of the screen to jump between pages.
          </li>
          <li>
            <i class="fas fa-li fa-expand-arrows-alt"></i> Click the expand button to experience our magazine in fullscreen.
          </li>
        </ul>
      </section>

      <!-- PAGES WITH TRANSITIONS -->
      <section
        class="pages"
        :class="'direction-'+(activePage > previousPage ? 'next' : 'previous')"
        :style="{perspective: perspective}"
        v-if="zoomImage === null"
      >
        <transition
          :duration="viewportOrientation === 'landscape' ? 1500 : 300"
          v-for="(page, i) in data.full_pages"
          :key="i"
          :name="viewportOrientation === 'landscape' ? (i < activePage ? 'left' : 'right') : 'hide'"
        >
          <div class="page"
              :class="[`layout-${data.page_size_id}`, i%2===0?'odd':'even']"
              :style="{zIndex: i<activePage ? 1000 + i : 1000 - i}"
              v-if="i === activePage || (i === activePage - 1 && viewportOrientation === 'landscape')"
              @dblclick="toggleZoom(i)"
              v-touch:swipe.right="previous"
              v-touch:swipe.left="next"
              title="Double click to zoom"
              draggable="false">
            <img
              :src="page.medium"
            />
          </div>
        </transition>
      </section>

      <!-- ZOOMED IN IMAGE -->
      <section class="zoom-page" v-else>
        <!-- IMAGE LOADING -->
        <i class="fas fa-spin fa-fw fa-sync centered" v-if="zoomImageLoading"></i>

        <!-- CLOSE ZOOMED IN IMAGE -->
        <i class="fas fa-times btn-close" title="Return to Page Turner" @click="zoomImage = null"></i>

        <!-- IMAGE -->
        <img
          :src="zoomImage"
          alt="Page"
          class="zoom-image"
          :style="{transform: `scale(${zoomLevel}) translate(-50%, -50%)`, visibility: zoomImageLoading ? 'hidden' : null, top: `${zoomImagePosition.top}px`, left: `${zoomImagePosition.left}px`}"
          @dblclick="zoomImage = null"
          @mousedown.prevent="startImageMove"
          @touchstart.prevent="startImageMove"
          @load="zoomImageLoading = false"
        />
      </section>

      <!-- THUMBNAIL BAR -->
      <transition name="hide">
        <section class="thumbs-container" v-if="showUI && zoomImage === null">
          <div class="thumbs-ui">
            <!-- CONTROLS -->
            <div class="controls">
              <!-- FIRST PAGE -->
              <a href="#1" class="btn fas fa-fast-backward" title="First Page"></a>

              <!-- AUTOMATIC PAGE TURNER -->
              <div
                class="btn fas"
                :class="{'fa-play': !isPlaying, 'fa-pause': isPlaying}"
                title="Toggle Automatic Page Turner"
                @click="togglePlay"
              ></div>

              <!-- FULLSCREEN -->
              <div
                class="btn fas"
                :class="{'fa-expand-arrows-alt': !isFullscreen, 'fa-compress-arrows-alt': isFullscreen}"
                id="fullscreenBtn"
                title="Toggle Fullscreen"
                @click="toggleFullscreen"
                v-if="fullscreenSupported"
              ></div>

              <!-- ZOOM -->
              <div
                class="btn fas"
                :class="{'fa-search-plus': !zoomImage, 'fa-search-minus':zoomImage !== null}"
                :title="zoomImage ? 'Zoom Out' : 'Zoom In'"
                @click="toggleZoom(activePage)"
                v-if="viewportOrientation === 'portrait'"
              ></div>

              <!-- SHARE -->
              <div class="btn fas fa-share" title="Share" @click="showSocial = true"></div>
            </div>

            <!-- PREVIOUS SET OF THUMBNAILS -->
            <i class="fas fa-chevron-left btn-prev" title="Previous Pages" @click="previousPages"></i>

            <!-- THUMBNAILS -->
            <div
              id="thumbs"
              ref="thumbContainer"
              v-touch:swipe.right="previousPages"
              v-touch:swipe.left="nextPages"
            >
              <a
                :href="`#${i+1}`"
                :style="{borderColor: i === activePage || i === activePage - 1 ? `${data.style.colour1}` : null}"
                :class="`thumb layout-${data.page_size_id}`"
                v-for="(page, i) in data.full_pages"
                :key="i"
              >
                <img :src="page.small" loading="lazy" />
                <span class="page-number">{{ i + 1 }}</span>
              </a>
            </div>

            <!-- NEXT SET OF THUMBNAILS -->
            <i class="fas fa-chevron-right btn-next" title="Next Pages" @click="nextPages"></i>
          </div>
        </section>
      </transition>

      <transition name="hide">
        <div class="social" @click="showSocial = false" v-if="showSocial">
          <div class="modal" @click.stop>
            <i class="fas fa-times btn-close" @click="showSocial = false"></i>
            <span class="title">Share Our Magazine</span>

            <a
              :href="`https://www.facebook.com/sharer/sharer.php?u=${url}`"
              target="_blank"
              class="btn-social btn-facebook"
            >
              <i class="fab fa-facebook fa-fw"></i> Facebook
            </a>
            <a
              :href="`https://twitter.com/home?status=Magazine%20-%20${url}`"
              target="_blank"
              class="btn-social btn-twitter"
            >
              <i class="fab fa-twitter fa-fw"></i> Twitter
            </a>
            <a
              :href="`https://plus.google.com/share?url=${url}`"
              target="_blank"
              class="btn-social btn-google-plus"
            >
              <i class="fab fa-google-plus-g fa-fw"></i> Google+
            </a>
            <a
              :href="`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=Magazine&summary=&source=`"
              target="_blank"
              class="btn-social btn-linkedin"
            >
              <i class="fab fa-linkedin fa-fw"></i> LinkedIn
            </a>
            <a :href="`mailto:?&subject=Magazine&body=${url}`" class="btn-social btn-email">
              <i class="fas fa-envelope fa-fw"></i> E-Mail
            </a>
          </div>
        </div>
      </transition>

      <!-- ANALYTICS CONSENT -->
      <transition name="hide">
        <aside class="consent" v-if="consent === null">
          <h2 class="consent-heading">Help us improve our digital magazines</h2>
          <p class="consent-text">
            We use cookies and similar technologies to understand how our
            magazines are read and improve your experience. We do not collect
            personal information, and you can decline and continue reading as
            normal.
          </p>
          <div class="consent-actions">
            <button class="consent-btn consent-decline" @click="setConsent(false)">
              Decline
            </button>
            <button
              class="consent-btn consent-accept"
              :style="acceptStyle"
              @click="setConsent(true)"
            >
              Accept
            </button>
          </div>
        </aside>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import easyScroll from 'easy-scroll'
import ReadingTracker from './tracking'

// Remembers the reader's consent choice on this device so they are only asked once.
const CONSENT_KEY = 'pt_consent'

// Google Analytics (GA4) measurement ID — loaded only after consent is granted.
const GA_MEASUREMENT_ID = 'G-X6VXMPDPF1'

export default {
  data: function () {
    const activePage = window.location.hash
      ? parseInt(window.location.hash.replace('#', '')) - 1
      : 0

    return {
      alias: window.location.pathname.replace('/', ''),
      loading: true,
      data: null,
      tracker: null,
      consent: null,
      activePage: activePage,
      previousPage: activePage,
      perspective: `${window.innerHeight}px`,
      isFullscreen: false,
      isPlaying: false,
      zoomImage: null,
      zoomImageLoading: true,
      zoomLevel: 0.4,
      zoomMoving: false,
      playTimer: null,
      showUI: true,
      showSocial: false,
      viewportOrientation:
        window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
      previousMousePosition: { x: 0, y: 0 },
      zoomImagePosition: { top: 0, left: 0 },
      touches: [],
      lastPinchFactor: 0,
      fullscreenSupported:
        document.body.requestFullscreen ||
        document.body.msRequestFullscreen ||
        document.body.mozRequestFullScreen ||
        document.body.webkitRequestFullscreen,
      url: encodeURIComponent(
        `${location.protocol}//${location.host}${location.pathname}`
      )
    }
  },
  created: function () {
    this.init()

    window.onhashchange = this.hashChange
    window.onresize = this.resize
    window.onkeyup = this.keyUp
    document.onfullscreenchange = this.fullscreenChanged

    document.onpointermove = this.mouseMoved

    document.onpointerup = this.stopImageMove
    document.onpointerleave = this.stopImageMove
    document.ontouchend = this.stopImageMove

    document.ontouchend = this.stopImageMove
    document.ontouchmove = this.mouseMoved
  },
  computed: {
    // The Accept button is tinted with the publication's brand colour, which can
    // be anything — including white. Pick a readable text colour and add a border
    // for very light brands so the button is never white-on-white on the banner.
    acceptStyle: function () {
      const colour =
        this.data?.style?.colour1_page_turners ||
        this.data?.style?.colour1 ||
        '#333'
      const light = this.isLightColour(colour)
      return {
        backgroundColor: colour,
        color: light ? '#222' : '#fff',
        border: light ? '1px solid rgba(0, 0, 0, 0.15)' : 'none'
      }
    }
  },
  methods: {
    init: function () {
      // Nothing to load
      if (!this.alias) {
        this.loading = false
        return
      }

      // Fetch the data from the API
      axios.get(process.env.VUE_APP_API_URL + this.alias).then(
        (response) => {
          this.data = response.data
          document.title = `${response.data.name} - Page Turner`
          this.loading = false
          this.applyConsent()
        },
        () => {
          this.loading = false
        }
      )
    },
    startTracking: function (useStoredId) {
      this.tracker = new ReadingTracker(
        process.env.VUE_APP_API_URL,
        this.alias,
        this.data.full_pages.length,
        useStoredId
      )
      this.tracker.start(this.visiblePages())
    },
    // Act on a previously saved choice; if none, the banner is shown.
    applyConsent: function () {
      let stored = null
      try {
        stored = window.localStorage.getItem(CONSENT_KEY)
      } catch (e) {
        stored = null
      }

      if (stored === 'granted') {
        this.consent = true
        this.enableAnalytics()
        this.startTracking(true)
      } else if (stored === 'denied') {
        this.consent = false
        this.startTracking(false)
      }
      // Otherwise consent stays null and the banner is displayed.
    },
    // Record the reader's choice and begin tracking accordingly.
    setConsent: function (granted) {
      this.consent = granted
      try {
        window.localStorage.setItem(CONSENT_KEY, granted ? 'granted' : 'denied')
      } catch (e) {
        // Storage blocked — honour the choice for this visit only.
      }
      if (granted) {
        this.enableAnalytics()
      }
      this.startTracking(granted)
    },
    // True when a hex colour is light enough to need dark text on top of it.
    isLightColour: function (colour) {
      const hex = String(colour).trim().replace('#', '')
      const full =
        hex.length === 3
          ? hex
              .split('')
              .map((c) => c + c)
              .join('')
          : hex
      if (!/^[0-9a-fA-F]{6}$/.test(full)) {
        return false
      }
      const r = parseInt(full.slice(0, 2), 16)
      const g = parseInt(full.slice(2, 4), 16)
      const b = parseInt(full.slice(4, 6), 16)
      // Perceived luminance (ITU-R BT.601), 0 (black) to 1 (white).
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.7
    },
    // Load Google Analytics (GA4). Called only once consent is granted, so the
    // gtag script and its cookies never touch the page for readers who decline.
    enableAnalytics: function () {
      if (window.gtag) {
        return
      }

      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
      document.head.appendChild(script)

      window.dataLayer = window.dataLayer || []
      window.gtag = function () {
        window.dataLayer.push(arguments)
      }
      window.gtag('js', new Date())
      window.gtag('config', GA_MEASUREMENT_ID)
    },
    // The 1-indexed page numbers currently on screen (a spread in landscape).
    visiblePages: function () {
      const pages = [this.activePage + 1]
      if (this.viewportOrientation === 'landscape' && this.activePage > 0) {
        pages.push(this.activePage)
      }
      return pages
    },
    resize: function () {
      this.perspective = `${window.innerHeight}px`
      this.viewportOrientation =
        window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'

      if (
        this.activePage % 2 !== 0 &&
        this.viewportOrientation === 'landscape'
      ) {
        this.activePage++
      }
    },
    keyUp: function (e) {
      if (e.which === 39 || e.which === 68) {
        this.next()
      } else if (e.which === 37 || e.which === 65) {
        this.previous()
      }
    },
    hashChange: function () {
      this.previousPage = this.activePage

      // Make the new pages visible
      this.activePage = parseInt(window.location.hash.replace('#', '')) - 1
      if (
        this.activePage % 2 !== 0 &&
        this.viewportOrientation === 'landscape'
      ) {
        this.activePage++
      }

      if (this.tracker) {
        this.tracker.trackPages(this.visiblePages())
      }
    },
    next: function (e) {
      const id = Math.min(
        this.activePage + (this.viewportOrientation === 'portrait' ? 1 : 2),
        this.data.full_pages.length
      )

      if (this.isPlaying && !e) {
        this.showUI = false
        if (id === this.data.full_pages.length) {
          window.location.hash = 1
          return
        }
      }

      window.location.hash = id + 1
    },
    previous: function () {
      const id = Math.max(
        this.activePage - (this.viewportOrientation === 'portrait' ? 1 : 2),
        0
      )
      window.location.hash = id + 1
    },
    togglePlay: function () {
      this.isPlaying = !this.isPlaying
      clearInterval(this.playTimer)

      if (!this.isPlaying) {
        this.showUI = true
        return
      }

      this.playTimer = setInterval(this.next, 5000)
    },
    toggleFullscreen: function () {
      if (this.isFullscreen) {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
        this.isFullscreen = false
        return
      }

      if (this.$refs.container.requestFullscreen) {
        this.$refs.container.requestFullscreen()
      } else if (this.$refs.container.mozRequestFullScreen) {
        this.$refs.container.mozRequestFullScreen()
      } else if (this.$refs.container.webkitRequestFullscreen) {
        this.$refs.container.webkitRequestFullscreen()
      } else if (this.$refs.container.msRequestFullscreen) {
        this.$refs.container.msRequestFullscreen()
      }
      this.isFullscreen = true
    },
    toggleZoom: function (id) {
      // Leave zoomed in state
      if (this.zoomImage) {
        this.zoomImage = null
        this.zoomMoving = false
        return
      }

      // Enter zoomed in state
      for (let i = 0; i < this.data.full_pages.length; i++) {
        if (i === id) {
          this.zoomImagePosition = {
            top: window.innerHeight / 2,
            left: window.innerWidth / 2
          }
          this.zoomImageLoading = true
          this.zoomLevel = 0.4
          this.zoomImage = this.data.full_pages[i].large
          clearInterval(this.playTimer)
          this.isPlaying = false
          break
        }
      }
    },
    fullscreenChanged: function (e) {
      this.isFullscreen = document.fullscreenElement !== null
    },
    mouseMoved: function (e) {
      e.preventDefault()

      clearInterval(this.playTimer)
      if (this.isPlaying) {
        this.playTimer = setInterval(this.next, 5000)
        this.showUI = true
        return
      }

      if (this.zoomImage !== null) {
        let touches = []
        if (e.touches) {
          touches = e.touches
        } else {
          for (const touch in this.touches) {
            touches.push(this.touches[touch])
          }
        }

        // Pan
        if (touches.length === 1 && this.zoomMoving) {
          const differenceX = e.pageX - this.previousMousePosition.x
          const differenceY = e.pageY - this.previousMousePosition.y
          this.previousMousePosition = { x: e.pageX, y: e.pageY }
          this.zoomImagePosition.left =
            this.zoomImagePosition.left + differenceX
          this.zoomImagePosition.top = this.zoomImagePosition.top + differenceY

          // Zoom
        } else if (touches.length > 1) {
          const dist = Math.sqrt(
            (touches[0].pageX - touches[1].pageX) *
              (touches[0].pageX - touches[1].pageX) +
              (touches[0].pageY - touches[1].pageY) *
                (touches[0].pageY - touches[1].pageY)
          )

          const pinchFactor = 1 / (window.innerWidth / dist)
          if (this.lastPinchFactor === 0) {
            this.lastPinchFactor = pinchFactor
          }

          const difference = this.lastPinchFactor - pinchFactor
          this.zoomLevel = Math.max(
            Math.min(this.zoomLevel - difference * 2, 1),
            0.1
          )

          this.lastPinchFactor = pinchFactor
        }
      }
    },
    startImageMove: function (e) {
      this.previousMousePosition = { x: e.pageX, y: e.pageY }
      if (!e.touches) {
        this.touches[e.pointerId] = { pageX: e.pageX, pageY: e.pageY }
      }
      this.zoomMoving = true
    },
    stopImageMove: function (e) {
      this.zoomMoving = false
      this.lastPinchFactor = 0
      delete this.touches[e.pointerId]
    },
    changeZoom: function (e) {
      e.preventDefault()
      e.stopImmediatePropagation()

      if (e.wheelDelta < 0 || e.detail > 0) {
        this.zoomLevel = Math.min(this.zoomLevel + 0.03, 1)
      } else {
        this.zoomLevel = Math.max(this.zoomLevel - 0.03, 0.1)
      }
    },
    previousPages: function () {
      easyScroll({
        scrollableDomEle: this.$refs.thumbContainer,
        direction: 'right',
        duration: 600,
        easingPreset: 'easeInQuad',
        scrollAmount: -this.$refs.thumbContainer.clientWidth
      })
    },
    nextPages: function () {
      easyScroll({
        scrollableDomEle: this.$refs.thumbContainer,
        direction: 'right',
        duration: 600,
        easingPreset: 'easeInQuad',
        scrollAmount: this.$refs.thumbContainer.clientWidth
      })
    }
  }
}
</script>

<style scoped>
.consent {
  position: fixed;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  z-index: 3000;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: calc(100% - 2rem);
  max-width: 32rem;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  background: #fff;
  color: #222;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.25);
  font-size: 0.9rem;
  line-height: 1.4;
}

.consent-heading {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
}

.consent-text {
  margin: 0;
}

.consent-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.consent-btn {
  cursor: pointer;
  border: none;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.consent-decline {
  background: #eee;
  color: #333;
}

.consent-accept {
  color: #fff;
}
</style>
